import { TaskTemplate, PartnerRecommendation } from '../../types/launchpad';

// Partner recommendations for different regions
const bankingPartners: PartnerRecommendation[] = [
  {
    name: 'Chase Business Banking',
    description: 'Chase offers business checking accounts with low fees, mobile banking, and merchant services.',
    website_url: 'https://www.chase.com/business/banking',
    regions: ['US'],
    referral_url: 'https://www.chase.com/referafriend',
    commission_info: '$50 per new account',
    category: 'banking'
  },
  {
    name: 'Tide Business Banking',
    description: 'Tide offers business current accounts with no monthly fees, easy integration with accounting software, and dedicated support.',
    website_url: 'https://www.tide.co/business-account/',
    regions: ['UK'],
    referral_url: 'https://www.tide.co/refer-and-earn/',
    commission_info: '£50 per new account',
    category: 'banking'
  }
];

const accountingPartners: PartnerRecommendation[] = [
  {
    name: 'QuickBooks',
    description: 'Intuit QuickBooks is accounting software that helps small businesses manage invoices, track expenses, and prepare for tax time.',
    website_url: 'https://quickbooks.intuit.com/',
    regions: ['US', 'UK', 'Global'],
    referral_url: 'https://quickbooks.intuit.com/affiliate/',
    commission_info: 'Commission on subscription plans',
    category: 'accounting'
  },
  {
    name: 'Xero',
    description: 'Xero is cloud-based accounting software for small businesses with features for invoicing, inventory, and payroll.',
    website_url: 'https://www.xero.com/',
    regions: ['US', 'UK', 'Global'],
    referral_url: 'https://www.xero.com/partners/',
    commission_info: 'Commission on subscription plans',
    category: 'accounting'
  }
];

const insurancePartners: PartnerRecommendation[] = [
  {
    name: 'Hiscox Business Insurance',
    description: 'Hiscox offers customized business insurance including liability, property, and professional insurance.',
    website_url: 'https://www.hiscox.com/small-business-insurance',
    regions: ['US', 'UK'],
    referral_url: 'https://www.hiscox.com/partners',
    commission_info: 'Commission on new policies',
    category: 'insurance'
  },
  {
    name: 'Next Insurance',
    description: 'Next Insurance provides tailored insurance for small businesses with instant coverage and certificates.',
    website_url: 'https://www.nextinsurance.com/',
    regions: ['US'],
    referral_url: 'https://www.nextinsurance.com/partners/',
    commission_info: 'Commission on new policies',
    category: 'insurance'
  }
];

const legalPartners: PartnerRecommendation[] = [
  {
    name: 'LegalZoom',
    description: 'LegalZoom offers legal services for business formation, intellectual property protection, and compliance.',
    website_url: 'https://www.legalzoom.com/business/',
    regions: ['US'],
    referral_url: 'https://www.legalzoom.com/affiliates/',
    commission_info: 'Commission on services purchased',
    category: 'legal'
  },
  {
    name: 'Rocket Lawyer',
    description: 'Rocket Lawyer provides legal documents, attorney advice, and business incorporation services.',
    website_url: 'https://www.rocketlawyer.com/business-and-contracts.rl',
    regions: ['US', 'UK'],
    referral_url: 'https://www.rocketlawyer.com/affiliates.rl',
    commission_info: 'Commission on subscription plans',
    category: 'legal'
  }
];

// Default task templates for the first 365 days of business launch
export const defaultTaskTemplates: TaskTemplate[] = [
  // PLANNING PHASE - Days 1-30
  {
    title: 'Define your business vision and mission',
    description: 'Create a clear statement of what your business aims to achieve and the values it stands for.',
    category: 'admin',
    priority: 'high',
    scheduled_day: 1,
    resources: [
      'https://www.sba.gov/business-guide/plan-your-business/write-your-business-plan',
      'https://www.entrepreneur.com/article/247577'
    ]
  },
  {
    title: 'Conduct market research',
    description: 'Research your target market, competitors, and industry trends to validate your business idea.',
    category: 'marketing',
    priority: 'high',
    scheduled_day: 3,
    resources: [
      'https://www.sba.gov/business-guide/plan-your-business/market-research-competitive-analysis',
      'https://www.hubspot.com/market-research'
    ]
  },
  {
    title: 'Create a business plan',
    description: 'Develop a comprehensive business plan including executive summary, company description, market analysis, organization structure, product line, marketing strategy, funding request, and financial projections.',
    category: 'admin',
    priority: 'high',
    scheduled_day: 7,
    resources: [
      'https://www.sba.gov/business-guide/plan-your-business/write-your-business-plan',
      'https://www.bplans.com/sample-business-plans/'
    ]
  },
  {
    title: 'Choose a business name',
    description: 'Select a name for your business that is memorable, reflects your brand, and is available for registration.',
    category: 'admin',
    priority: 'high',
    scheduled_day: 10,
    resources: [
      'https://www.uspto.gov/trademarks-application-process/search-trademark-database',
      'https://www.namecheckr.com/'
    ]
  },
  {
    title: 'Register your domain name',
    description: 'Secure a domain name that matches or closely relates to your business name.',
    category: 'marketing',
    priority: 'medium',
    scheduled_day: 12,
    resources: [
      'https://domains.google/',
      'https://www.namecheap.com/'
    ]
  },
  {
    title: 'Choose a business structure',
    description: 'Decide on the legal structure for your business (sole proprietorship, LLC, corporation, etc.).',
    category: 'legal',
    priority: 'high',
    scheduled_day: 14,
    resources: [
      'https://www.sba.gov/business-guide/launch-your-business/choose-business-structure',
      'https://www.irs.gov/businesses/small-businesses-self-employed/business-structures'
    ],
    partner_recommendations: legalPartners
  },
  {
    title: 'Register your business',
    description: 'Register your business with the appropriate government agencies.',
    category: 'legal',
    priority: 'high',
    scheduled_day: 16,
    resources: [
      'https://www.sba.gov/business-guide/launch-your-business/register-your-business',
      'https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online'
    ],
    partner_recommendations: legalPartners
  },
  {
    title: 'Obtain necessary licenses and permits',
    description: 'Research and apply for all required business licenses and permits for your location and industry.',
    category: 'legal',
    priority: 'high',
    scheduled_day: 20,
    resources: [
      'https://www.sba.gov/business-guide/launch-your-business/apply-licenses-permits',
      'https://www.usa.gov/business-licenses'
    ],
    partner_recommendations: legalPartners
  },
  {
    title: 'Open a business bank account',
    description: 'Set up a separate bank account for your business finances.',
    category: 'finance',
    priority: 'high',
    scheduled_day: 22,
    resources: [
      'https://www.sba.gov/business-guide/launch-your-business/open-business-bank-account',
      'https://www.nerdwallet.com/best/small-business/business-checking-accounts'
    ],
    partner_recommendations: bankingPartners
  },
  {
    title: 'Set up accounting software',
    description: 'Choose and implement accounting software to track income, expenses, and prepare for taxes.',
    category: 'finance',
    priority: 'high',
    scheduled_day: 24,
    resources: [
      'https://www.pcmag.com/picks/the-best-small-business-accounting-software',
      'https://www.capterra.com/accounting-software/'
    ],
    partner_recommendations: accountingPartners
  },
  {
    title: 'Secure business insurance',
    description: 'Research and obtain appropriate insurance coverage for your business.',
    category: 'finance',
    priority: 'high',
    scheduled_day: 26,
    resources: [
      'https://www.sba.gov/business-guide/launch-your-business/get-business-insurance',
      'https://www.insureon.com/small-business-insurance'
    ],
    partner_recommendations: insurancePartners
  },
  {
    title: 'Create a business email address',
    description: 'Set up a professional email address with your business domain.',
    category: 'operations',
    priority: 'medium',
    scheduled_day: 28,
    resources: [
      'https://workspace.google.com/',
      'https://www.microsoft.com/en-us/microsoft-365/business/'
    ]
  },
  {
    title: 'Develop your brand identity',
    description: 'Create your logo, color scheme, typography, and other visual elements of your brand.',
    category: 'marketing',
    priority: 'medium',
    scheduled_day: 30,
    resources: [
      'https://www.canva.com/create/logos/',
      'https://99designs.com/'
    ]
  },
  
  // SETUP PHASE - Days 31-90
  {
    title: 'Create a business website',
    description: 'Develop a professional website for your business.',
    category: 'marketing',
    priority: 'high',
    scheduled_day: 35,
    resources: [
      'https://www.wix.com/',
      'https://www.squarespace.com/',
      'https://wordpress.org/'
    ]
  },
  {
    title: 'Set up social media accounts',
    description: 'Create business profiles on relevant social media platforms.',
    category: 'marketing',
    priority: 'medium',
    scheduled_day: 38,
    resources: [
      'https://sproutsocial.com/insights/social-media-for-business/',
      'https://buffer.com/library/social-media-accounts-for-business/'
    ]
  },
  {
    title: 'Develop your product or service offering',
    description: 'Finalize the details of your products or services, including pricing, features, and benefits.',
    category: 'product',
    priority: 'high',
    scheduled_day: 42,
    resources: [
      'https://www.productplan.com/learn/product-development-process/',
      'https://www.shopify.com/blog/product-development'
    ]
  },
  {
    title: 'Create a marketing plan',
    description: 'Develop a comprehensive marketing strategy to reach your target audience.',
    category: 'marketing',
    priority: 'high',
    scheduled_day: 45,
    resources: [
      'https://www.hubspot.com/marketing-plan-template',
      'https://www.smartinsights.com/marketing-planning/create-a-marketing-plan/'
    ]
  },
  {
    title: 'Set up payment processing',
    description: 'Implement systems to accept payments from customers.',
    category: 'finance',
    priority: 'high',
    scheduled_day: 50,
    resources: [
      'https://www.shopify.com/payment-gateways',
      'https://www.nerdwallet.com/article/small-business/credit-card-processing-small-business'
    ]
  },
  {
    title: 'Develop a customer service policy',
    description: 'Create guidelines for how your business will handle customer inquiries, complaints, and feedback.',
    category: 'operations',
    priority: 'medium',
    scheduled_day: 55,
    resources: [
      'https://www.helpscout.com/blog/customer-service-policy/',
      'https://www.zendesk.com/blog/customer-service-policy/'
    ]
  },
  {
    title: 'Set up inventory management',
    description: 'Implement a system to track and manage your inventory (if applicable).',
    category: 'operations',
    priority: 'medium',
    scheduled_day: 60,
    resources: [
      'https://www.shopify.com/blog/inventory-management',
      'https://www.netsuite.com/portal/resource/articles/inventory-management/inventory-management-guide.shtml'
    ]
  },
  {
    title: 'Create a sales strategy',
    description: 'Develop a plan for how you will sell your products or services and reach your sales targets.',
    category: 'sales',
    priority: 'high',
    scheduled_day: 65,
    resources: [
      'https://www.salesforce.com/blog/how-to-create-a-sales-plan/',
      'https://www.pipedrive.com/en/blog/sales-strategy'
    ]
  },
  {
    title: 'Develop a content marketing strategy',
    description: 'Plan what content you will create to attract and engage your target audience.',
    category: 'marketing',
    priority: 'medium',
    scheduled_day: 70,
    resources: [
      'https://contentmarketinginstitute.com/developing-a-strategy/',
      'https://blog.hubspot.com/marketing/content-marketing-plan'
    ]
  },
  {
    title: 'Set up email marketing',
    description: 'Implement an email marketing system to communicate with customers and prospects.',
    category: 'marketing',
    priority: 'medium',
    scheduled_day: 75,
    resources: [
      'https://mailchimp.com/resources/email-marketing-guide/',
      'https://www.constantcontact.com/blog/email-marketing-guide/'
    ]
  },
  {
    title: 'Hire key employees or contractors',
    description: 'Identify and recruit the talent you need to support your business operations.',
    category: 'hr',
    priority: 'medium',
    scheduled_day: 80,
    resources: [
      'https://www.indeed.com/hire/how-to-hire-employees',
      'https://www.upwork.com/hire/'
    ]
  },
  {
    title: 'Develop an employee handbook',
    description: 'Create a document outlining company policies, procedures, and expectations for employees.',
    category: 'hr',
    priority: 'low',
    scheduled_day: 85,
    resources: [
      'https://www.shrm.org/resourcesandtools/tools-and-samples/policies/pages/default.aspx',
      'https://gusto.com/blog/people-management/small-business-employee-handbook'
    ]
  },
  {
    title: 'Set up business operations',
    description: 'Establish the day-to-day processes and systems needed to run your business efficiently.',
    category: 'operations',
    priority: 'high',
    scheduled_day: 90,
    resources: [
      'https://www.score.org/resource/how-set-basic-business-operations',
      'https://www.business.org/business/startup/how-to-start-a-business-operations-guide/'
    ]
  },
  
  // LAUNCH PHASE - Days 91-180
  {
    title: 'Conduct beta testing',
    description: 'Test your product or service with a small group of users to gather feedback before full launch.',
    category: 'product',
    priority: 'high',
    scheduled_day: 95,
    resources: [
      'https://www.productplan.com/glossary/beta-testing/',
      'https://www.userinterviews.com/blog/beta-testing-guide'
    ]
  },
  {
    title: 'Analyze beta testing feedback',
    description: 'Review and analyze feedback from beta testers to identify improvements.',
    category: 'product',
    priority: 'high',
    scheduled_day: 100,
    resources: [
      'https://www.productplan.com/learn/customer-feedback-analysis/',
      'https://www.hotjar.com/blog/user-feedback/'
    ]
  },
  {
    title: 'Implement pre-launch marketing campaign',
    description: 'Execute marketing activities to build anticipation for your business launch.',
    category: 'marketing',
    priority: 'high',
    scheduled_day: 105,
    resources: [
      'https://www.shopify.com/blog/product-launch-checklist',
      'https://neilpatel.com/blog/product-launch-guide/'
    ]
  },
  {
    title: 'Finalize pricing strategy',
    description: 'Set the final prices for your products or services based on costs, competition, and market research.',
    category: 'finance',
    priority: 'high',
    scheduled_day: 110,
    resources: [
      'https://www.priceintelligently.com/blog/bid/163986/a-complete-guide-to-pricing-strategy',
      'https://hbr.org/2016/08/a-quick-guide-to-value-based-pricing'
    ]
  },
  {
    title: 'Prepare sales materials',
    description: 'Create the sales collateral needed to support your sales efforts.',
    category: 'sales',
    priority: 'medium',
    scheduled_day: 115,
    resources: [
      'https://blog.hubspot.com/sales/sales-collateral',
      'https://www.proposify.com/blog/sales-collateral'
    ]
  },
  {
    title: 'Train customer service team',
    description: 'Ensure your team is prepared to provide excellent customer service.',
    category: 'hr',
    priority: 'medium',
    scheduled_day: 120,
    resources: [
      'https://www.helpscout.com/blog/customer-service-training/',
      'https://www.zendesk.com/blog/customer-service-training/'
    ]
  },
  {
    title: 'Conduct final quality assurance',
    description: 'Perform thorough testing of your product or service to ensure it meets quality standards.',
    category: 'product',
    priority: 'high',
    scheduled_day: 125,
    resources: [
      'https://www.productplan.com/glossary/quality-assurance/',
      'https://www.guru99.com/all-about-quality-assurance.html'
    ]
  },
  {
    title: 'Prepare for launch day',
    description: 'Create a detailed plan for your official business launch day.',
    category: 'admin',
    priority: 'high',
    scheduled_day: 130,
    resources: [
      'https://www.shopify.com/blog/product-launch-checklist',
      'https://blog.hubspot.com/marketing/product-launch-checklist'
    ]
  },
  {
    title: 'Official business launch',
    description: 'Execute your launch plan and officially open your business to the public.',
    category: 'admin',
    priority: 'critical',
    scheduled_day: 135,
    resources: [
      'https://www.entrepreneur.com/article/322396',
      'https://www.inc.com/guides/2010/12/how-to-launch-a-business.html'
    ]
  },
  {
    title: 'Monitor initial customer feedback',
    description: 'Collect and analyze feedback from your first customers.',
    category: 'product',
    priority: 'high',
    scheduled_day: 140,
    resources: [
      'https://www.helpscout.com/blog/customer-feedback/',
      'https://www.qualtrics.com/experience-management/customer/collecting-customer-feedback/'
    ]
  },
  {
    title: 'Implement post-launch marketing activities',
    description: 'Execute marketing campaigns to maintain momentum after launch.',
    category: 'marketing',
    priority: 'high',
    scheduled_day: 145,
    resources: [
      'https://www.shopify.com/blog/marketing-strategy',
      'https://neilpatel.com/blog/marketing-campaign/'
    ]
  },
  {
    title: 'Evaluate initial sales performance',
    description: 'Analyze your sales data to identify trends and areas for improvement.',
    category: 'sales',
    priority: 'high',
    scheduled_day: 150,
    resources: [
      'https://www.pipedrive.com/en/blog/sales-analysis',
      'https://blog.hubspot.com/sales/sales-metrics'
    ]
  },
  {
    title: 'Optimize website based on user behavior',
    description: 'Use analytics data to improve your website\'s performance and user experience.',
    category: 'marketing',
    priority: 'medium',
    scheduled_day: 160,
    resources: [
      'https://www.crazyegg.com/blog/website-optimization/',
      'https://moz.com/learn/seo/conversion-rate-optimization'
    ]
  },
  {
    title: 'Implement customer retention strategies',
    description: 'Develop and execute plans to encourage repeat business from your customers.',
    category: 'marketing',
    priority: 'high',
    scheduled_day: 170,
    resources: [
      'https://www.helpscout.com/blog/customer-retention-strategies/',
      'https://blog.hubspot.com/service/customer-retention-strategies'
    ]
  },
  {
    title: 'Conduct first month business review',
    description: 'Evaluate your business performance after the first month of operations.',
    category: 'admin',
    priority: 'high',
    scheduled_day: 180,
    resources: [
      'https://www.score.org/resource/how-conduct-monthly-review-your-business',
      'https://www.businessnewsdaily.com/10757-business-performance-review.html'
    ]
  },
  
  // GROWTH PHASE - Days 181-365
  {
    title: 'Develop a customer referral program',
    description: 'Create a system to encourage and reward customer referrals.',
    category: 'marketing',
    priority: 'medium',
    scheduled_day: 190,
    resources: [
      'https://www.referralcandy.com/blog/referral-program/',
      'https://www.shopify.com/blog/referral-marketing'
    ]
  },
  {
    title: 'Expand product or service offerings',
    description: 'Identify opportunities to add new products or services based on customer feedback and market demand.',
    category: 'product',
    priority: 'medium',
    scheduled_day: 210,
    resources: [
      'https://www.productplan.com/learn/product-expansion/',
      'https://www.shopify.com/blog/product-line-expansion'
    ]
  },
  {
    title: 'Implement search engine optimization (SEO)',
    description: 'Optimize your website to improve visibility in search engine results.',
    category: 'marketing',
    priority: 'medium',
    scheduled_day: 220,
    resources: [
      'https://moz.com/beginners-guide-to-seo',
      'https://www.semrush.com/blog/seo-strategy/'
    ]
  },
  {
    title: 'Explore partnership opportunities',
    description: 'Identify potential strategic partners to help grow your business.',
    category: 'sales',
    priority: 'medium',
    scheduled_day: 240,
    resources: [
      'https://www.forbes.com/sites/allbusiness/2018/08/29/business-partnerships-strategy-success/',
      'https://www.entrepreneur.com/article/241989'
    ]
  },
  {
    title: 'Implement customer loyalty program',
    description: 'Create a program to reward and retain your most valuable customers.',
    category: 'marketing',
    priority: 'medium',
    scheduled_day: 260,
    resources: [
      'https://www.shopify.com/blog/customer-loyalty-program',
      'https://www.bigcommerce.com/blog/customer-loyalty-program/'
    ]
  },
  {
    title: 'Conduct quarterly business review',
    description: 'Evaluate your business performance after the first quarter of operations.',
    category: 'admin',
    priority: 'high',
    scheduled_day: 270,
    resources: [
      'https://www.score.org/resource/how-conduct-quarterly-review-your-business',
      'https://www.businessnewsdaily.com/10757-business-performance-review.html'
    ]
  },
  {
    title: 'Implement advanced analytics',
    description: 'Set up more sophisticated data analysis to inform business decisions.',
    category: 'operations',
    priority: 'medium',
    scheduled_day: 290,
    resources: [
      'https://www.tableau.com/learn/articles/business-analytics',
      'https://www.mckinsey.com/business-functions/mckinsey-analytics/our-insights/analytics-comes-of-age'
    ]
  },
  {
    title: 'Explore additional funding options',
    description: 'Research potential sources of funding to support business growth.',
    category: 'finance',
    priority: 'medium',
    scheduled_day: 310,
    resources: [
      'https://www.sba.gov/funding-programs',
      'https://www.fundable.com/learn/resources/guides/startup-guide/types-of-funding'
    ]
  },
  {
    title: 'Develop a scaling strategy',
    description: 'Create a plan for how to grow your business operations to meet increasing demand.',
    category: 'operations',
    priority: 'high',
    scheduled_day: 330,
    resources: [
      'https://www.entrepreneur.com/article/316093',
      'https://hbr.org/2016/06/scaling-your-company-without-losing-its-culture'
    ]
  },
  {
    title: 'Conduct annual business review',
    description: 'Perform a comprehensive evaluation of your first year in business.',
    category: 'admin',
    priority: 'critical',
    scheduled_day: 365,
    resources: [
      'https://www.score.org/resource/how-conduct-year-end-review',
      'https://www.inc.com/guides/2010/12/how-to-conduct-an-annual-review-of-your-business.html'
    ]
  }
];
